import { HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { catchError, switchMap } from 'rxjs/operators';
import { from, throwError } from 'rxjs';
import { environment } from '../../environments/environment';
import { AuthService } from '../services/auth.service';
import { GuestNudgeService } from '../services/guest-nudge.service';

export const authInterceptor: HttpInterceptorFn = (req, next) => {
    const authService = inject(AuthService);
    const nudgeService = inject(GuestNudgeService);
    const router = inject(Router);
    const token = authService.accessToken();
    const shouldAttach = req.url.startsWith(environment.apiBase);

    // Every API request gets `withCredentials: true` so the `ciq_session`
    // cookie travels both ways (Set-Cookie on login responses, Cookie
    // header on subsequent requests). CORS on the API echoes credentials.
    const authReq = shouldAttach
        ? req.clone({
              ...(token ? { setHeaders: { Authorization: `Bearer ${token}` } } : {}),
              withCredentials: true,
          })
        : req;

    return next(authReq).pipe(
        catchError((error: HttpErrorResponse) => {
            // The backend 403s with this code when the JWT's workspace claim no
            // longer matches live membership (the user was removed from the
            // workspace). Surfacing it as a generic failure leaves the user
            // stuck; only a fresh sign-in mints a token with a valid claim, so
            // open the app-root re-login dialog.
            if (error.status === 403 && error.error?.detail?.code === 'workspace_membership_inactive') {
                authService.promptMembershipInactive();
                return throwError(() => error);
            }

            if (error.status === 401) {
                if (authService.isGuest()) {
                    // Guest JWT expired. Guest access is retired (CIQ-1643), so
                    // this lands on sign-in rather than the removed /try page —
                    // AC2: anyone still mid-session at cutover is asked to sign
                    // in or create an account. Navigating to /try here after the
                    // route was deleted would strand them on a dead URL.
                    authService.logout({ redirect: false });
                    void router.navigate(['/login']);
                    return throwError(() => error);
                }

                // Never refresh-and-retry the auth endpoints themselves.
                // /auth/refresh handles its own 401 inside refreshTokens() (it
                // may adopt tokens from another tab's successful rotation), and
                // a 401 from /login means bad credentials, so retrying it with a
                // refreshed token cannot help and only risks a refresh loop.
                const isAuthEndpoint = req.url.includes('/auth/refresh') || req.url === `${environment.apiBase}/login`;
                if (isAuthEndpoint) {
                    return throwError(() => error);
                }

                // Attempt token refresh, then retry the original request.
                // refreshTokens() single-flights internally, so concurrent
                // 401s (and ensureFreshSession) share one rotation - no
                // refresh-token reuse, no family revocation.
                return from(authService.refreshTokens()).pipe(
                    switchMap(refreshed => {
                        if (!refreshed) {
                            // Definitive: refresh token invalid/expired → log out.
                            authService.handleAuthError();
                            return throwError(() => error);
                        }

                        // Retry with the new access token
                        const newToken = authService.accessToken();
                        const retryReq = shouldAttach
                            ? req.clone({
                                  ...(newToken ? { setHeaders: { Authorization: `Bearer ${newToken}` } } : {}),
                                  withCredentials: true,
                              })
                            : req;
                        return next(retryReq);
                    }),
                    catchError(() => {
                        // attemptRefresh threw = TRANSIENT failure (backend
                        // restarting / network blip / 5xx). Don't sign the user
                        // out over a momentary glitch - fail just this request;
                        // the next 401 will refresh once the backend is back.
                        return throwError(() => error);
                    })
                );
            }

            // Handle 403 guest-specific errors
            if (error.status === 403 && authService.isGuest()) {
                const code = error.error?.detail?.code;

                if (code === 'guest_session_expired') {
                    // Same cutover rule as the 401 branch above: sign-in, not
                    // the removed guest start page (CIQ-1643 AC2).
                    authService.logout({ redirect: false });
                    void router.navigate(['/login']);
                    return throwError(() => error);
                }

                if (code === 'guest_limit_reached') {
                    // Message limit hit - open conversion modal
                    nudgeService.openConversionModal();
                    return throwError(() => error);
                }

                if (code === 'guest_upgrade_required') {
                    // Feature restricted - show toast nudge
                    nudgeService.showUpgradeNudge(error.error?.detail?.message);
                    return throwError(() => error);
                }
            }

            return throwError(() => error);
        })
    );
};
